import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, ClipboardList, X } from "lucide-react";
import { toast } from "sonner";

const fmtDatum = (iso) => {
  if (!iso) return "–";
  const d = new Date(iso);
  return d.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
};

const schwereFarbe = (aktion) => {
  if (/(storniert|geloescht|geloescht|delete|deleted|freigegeben|angelegt|aufgehoben)/.test(aktion))
    return "destructive";
  if (/(geaendert|updated|erstellt|angelegt|import)/.test(aktion)) return "secondary";
  return "outline";
};

export default function AdminAuditlogPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    aktion: "", entitaet: "", benutzer_email: "", von: "", bis: "", limit: 200,
  });
  const [options, setOptions] = useState({ aktionen: [], entitaeten: [], benutzer: [] });

  const setF = (k, v) => setFilter((s) => ({ ...s, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => {
        if (v !== "" && v != null) params.set(k, v);
      });
      const { data } = await api.get(`/auditlog?${params.toString()}`);
      setRows(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => {
    api.get("/auditlog/aktionen").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const reset = () => setFilter({
    aktion: "", entitaet: "", benutzer_email: "", von: "", bis: "", limit: 200,
  });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <ClipboardList className="h-6 w-6" /> Audit-Log
        </h1>
        <p className="text-sm text-muted-foreground">
          Alle sicherheits- und stammdatenrelevanten Aktionen in der Anwendung.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filter</CardTitle>
          <CardDescription>Suche nach Aktion, Entität, Benutzer oder Zeitraum.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Aktion</Label>
              <Select value={filter.aktion || "__alle__"}
                onValueChange={(v) => setF("aktion", v === "__alle__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Alle Aktionen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__alle__">Alle Aktionen</SelectItem>
                  {options.aktionen.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Entität</Label>
              <Select value={filter.entitaet || "__alle__"}
                onValueChange={(v) => setF("entitaet", v === "__alle__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Alle Entitäten" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__alle__">Alle Entitäten</SelectItem>
                  {options.entitaeten.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Benutzer</Label>
              <Select value={filter.benutzer_email || "__alle__"}
                onValueChange={(v) => setF("benutzer_email", v === "__alle__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Alle Benutzer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__alle__">Alle Benutzer</SelectItem>
                  {options.benutzer.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Von</Label>
              <Input type="date" value={filter.von}
                onChange={(e) => setF("von", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Bis</Label>
              <Input type="date" value={filter.bis}
                onChange={(e) => setF("bis", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Anzahl</Label>
              <Input type="number" min="10" max="1000" value={filter.limit}
                onChange={(e) => setF("limit", Number(e.target.value))} />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="outline" onClick={reset}>
              <X className="h-4 w-4 mr-1" /> Filter zurücksetzen
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Ergebnisse ({rows.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">Zeitpunkt</TableHead>
                  <TableHead>Aktion</TableHead>
                  <TableHead>Entität</TableHead>
                  <TableHead>Benutzer</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center">
                      <Loader2 className="h-5 w-5 animate-spin inline" />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      Keine Einträge für die aktuellen Filter.
                    </TableCell>
                  </TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-num text-xs whitespace-nowrap">{fmtDatum(r.zeitpunkt)}</TableCell>
                    <TableCell>
                      <Badge variant={schwereFarbe(r.aktion)} className="font-mono text-[10px]">
                        {r.aktion}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{r.entitaet}</div>
                      {r.entitaet_id && (
                        <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[160px]">
                          {r.entitaet_id}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{r.benutzer_name || "System"}</div>
                      <div className="text-[10px] text-muted-foreground">{r.benutzer_email}</div>
                    </TableCell>
                    <TableCell className="max-w-[420px]">
                      <p className="text-xs whitespace-pre-wrap">{r.details}</p>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
